import {
  type FeatureFlagContext,
  buildPosthogGroupProperties,
  buildPosthogPersonProperties,
  toFeatureFlagEnvironment,
} from '@fordeer/feature-flags'
import { FORDEER_APPS } from '@fordeer/ui-components'
import { usePosthogStore } from '@store/posthog'
import posthog from 'posthog-js'
import { useEffect, useRef } from 'react'

const POSTHOG_INIT_RETRY_DELAY_MS = 1_000

interface UsePosthogInitOptions {
  shopId?: string | null
  shopDomain?: string | null
  planName?: string | null
  countryCode?: string | null
  partnerDevelopment?: boolean | string | null
  subscriptionCreatedAt?: string | null
  enabled?: boolean
}

export const usePosthogInit = ({
  shopId,
  shopDomain,
  planName,
  countryCode,
  partnerDevelopment,
  subscriptionCreatedAt,
  enabled = true,
}: UsePosthogInitOptions = {}) => {
  const activeFingerprintRef = useRef<string | null>(null)

  useEffect(() => {
    const { setLoading, setReady, reset } = usePosthogStore.getState()

    if (!enabled || !shopId) {
      activeFingerprintRef.current = null
      reset()
      return
    }

    const fingerprint = shopId
    activeFingerprintRef.current = fingerprint
    setLoading()
    let reloadStarted = false
    let targetingApplied = false
    // One automatic initialization retry per effect lifecycle. Later SDK
    // deliveries recover independently; context changes create a fresh budget.
    let retriedInitialization = false
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    const clearRetry = () => {
      // Cancelling a timer does not renew the lifecycle's retry budget.
      if (retryTimeout !== null) {
        clearTimeout(retryTimeout)
        retryTimeout = null
      }
    }
    const featureFlagContext: FeatureFlagContext = {
      app: FORDEER_APPS.Invoice,
      environment: toFeatureFlagEnvironment(
        import.meta.env.MODE,
        import.meta.env.VITE_VERCEL_ENV,
        import.meta.env.VITE_FEATURE_FLAG_ENVIRONMENT,
      ),
      shop: {
        id: shopId,
        domain: shopDomain,
        plan: planName,
        country: countryCode,
        partnerDevelopment,
        subscriptionCreatedAt,
      },
    }

    const handleFeatureFlags = (
      _enabledFlags: string[],
      _variants: Record<string, string | boolean>,
      context?: { errorsLoading?: boolean },
    ) => {
      // Only a /flags response reports a boolean `errorsLoading`. Cached
      // deliveries (subscription replay, overrides, enrollment updates) leave
      // it undefined, so they can never complete the reload started below.
      const errorsLoading = context?.errorsLoading
      if (
        activeFingerprintRef.current !== fingerprint ||
        !reloadStarted ||
        !targetingApplied ||
        typeof errorsLoading !== 'boolean'
      ) {
        return
      }
      if (!errorsLoading) clearRetry()
      setReady(errorsLoading)
    }

    let unsubscribe: (() => void) | undefined
    let unsubscribeReloading: (() => void) | undefined

    try {
      unsubscribe = posthog.onFeatureFlags(handleFeatureFlags)
      unsubscribeReloading = posthog.on('featureFlagsReloading', () => {
        if (activeFingerprintRef.current !== fingerprint) return
        reloadStarted = true
        setLoading()
      })
    } catch (error) {
      if (typeof unsubscribe === 'function') unsubscribe()
      console.warn('[Posthog] Failed to subscribe to feature flags', error)
      setReady(true)
      return
    }

    const scheduleRetry = () => {
      if (retriedInitialization) return
      retriedInitialization = true
      retryTimeout = setTimeout(() => {
        retryTimeout = null
        initializeFeatureFlags()
      }, POSTHOG_INIT_RETRY_DELAY_MS)
    }

    const initializeFeatureFlags = () => {
      if (activeFingerprintRef.current !== fingerprint) return
      reloadStarted = false
      setLoading()

      if (!targetingApplied) {
        // Identity/analytics is optional for shop-level rollout. Keep its
        // failure separate from required group and inline flag properties.
        try {
          posthog.identify(
            shopId,
            buildPosthogPersonProperties(featureFlagContext),
          )
        } catch (error) {
          console.warn('[Posthog] Failed to identify shop', error)
        }

        try {
          if (typeof posthog.group !== 'function') {
            throw new Error('PostHog group analytics is unavailable')
          }
          const groupProperties =
            buildPosthogGroupProperties(featureFlagContext)
          posthog.group('account', shopId, groupProperties)
          posthog.setGroupPropertiesForFlags(
            { account: groupProperties },
            false,
          )
          posthog.setPersonPropertiesForFlags(
            buildPosthogPersonProperties(featureFlagContext),
            false,
          )
          targetingApplied = true
        } catch (error) {
          console.warn('[Posthog] Failed to apply targeting', error)
          setReady(true)
          scheduleRetry()
          return
        }
      }

      try {
        posthog.reloadFeatureFlags()
      } catch (error) {
        // A reload failure does not invalidate an identity/group that already
        // succeeded. Keep the gate closed, but allow a later reload to recover.
        console.warn('[Posthog] Failed to reload feature flags', error)
        setReady(true)
        scheduleRetry()
      }
    }

    initializeFeatureFlags()

    return () => {
      clearRetry()
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
      if (typeof unsubscribeReloading === 'function') {
        unsubscribeReloading()
      }

      if (activeFingerprintRef.current === fingerprint) {
        activeFingerprintRef.current = null
        reset()
      }
    }
  }, [
    enabled,
    shopId,
    shopDomain,
    planName,
    countryCode,
    partnerDevelopment,
    subscriptionCreatedAt,
  ])
}
